<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { NButton, NEmpty, useMessage } from 'naive-ui'
import { getMaterialPreviewUrl, type MaterialGoods, type MaterialResource } from '@/material/web/api'
import { applyMaterialResourceToWorkspace } from '@/features/material-library/apply'
import { applyUiMaterialToWorkspace, type UiMaterialApplyProgress } from '@/features/material-library/uiApply'
import { canApplyMaterialCategory, isUiMaterialCategory } from '@/features/material-library/targets'
import { areMaterialResourcesAlreadyApplied, resolveMaterialUsageRoot } from '@/features/material-library/usage'
import MaterialPreviewLightbox from '@/material/web/MaterialPreviewLightbox.vue'
import MaterialPreviewSizeSelect from '@/material/web/MaterialPreviewSizeSelect.vue'
import { useWorkspaceStore } from '@/store/workspace'
import {
  buildMaterialResourceStableKey,
  getResolvableFlatAssetThumbs,
  getResourceExtra,
  isHorizontalCategory,
  type MaterialPreviewSizeOption,
  resolveFlatAssetPreviewResource,
  resolveFlatAssetResource,
  resolveMatchedMaterialSize,
  resolveMaterialPreviewSelectedSize,
  resolveThumbSizeOptions,
} from '@/material/web/previewUtils'

export type MaterialPreviewOverrideItem = {
  id: string
  url: string
  extra: string
}

const props = withDefaults(defineProps<{
  goods: MaterialGoods
  resources: MaterialResource[]
  topCategoryId: string
  collected: boolean
  previewLoading?: boolean
  showCollect?: boolean
  showThumbs?: boolean
  preferPreviewUrlOverride?: boolean
  previewUrlOverride?: string
  previewItemsOverride?: MaterialPreviewOverrideItem[]
  sizeOptionsOverride?: MaterialPreviewSizeOption[]
  applyOverride?: (() => Promise<void>) | null
}>(), {
  previewLoading: false,
  showCollect: true,
  showThumbs: true,
  preferPreviewUrlOverride: false,
  previewUrlOverride: '',
  previewItemsOverride: () => [],
  sizeOptionsOverride: () => [],
  applyOverride: null,
})

const emit = defineEmits<{
  toggleCollect: []
}>()

const selectedThumbId = ref('')
const selectedSize = ref('')
const lightboxOpen = ref(false)
const lightboxGalleryIndex = ref(0)
const applying = ref(false)
const applyProgressText = ref('')
const message = useMessage()
const workspace = useWorkspaceStore()

const thumbs = computed(() =>
  props.previewItemsOverride.length
    ? props.previewItemsOverride
    : getResolvableFlatAssetThumbs(props.resources, props.topCategoryId),
)
const thumbEntries = computed(() =>
  thumbs.value.map((item, index) => ({
    item,
    key:
      'url' in item
        ? [String(item.id ?? ''), item.url, item.extra, String(index)].join('|')
        : buildMaterialResourceStableKey(item as MaterialResource, index),
  })),
)
const selectedThumb = computed(() => {
  const selected = thumbEntries.value.find((entry) => entry.key === selectedThumbId.value) ?? null
  if (selected) return selected.item
  if (props.preferPreviewUrlOverride && props.previewUrlOverride) return null
  return thumbs.value[0] ?? null
})
const sizeOptions = computed(() =>
  props.sizeOptionsOverride.length
    ? props.sizeOptionsOverride
    : resolveThumbSizeOptions(
        props.resources,
        props.topCategoryId,
        selectedThumb.value as MaterialResource | null,
      ),
)
const sizeValues = computed(() => sizeOptions.value.map((item) => item.value))
const preferredSize = computed(() =>
  resolveMatchedMaterialSize(
    sizeValues.value,
    workspace.project?.screenHash.width ?? workspace.project?.Width ?? 0,
    workspace.project?.screenHash.height ?? workspace.project?.Height ?? 0,
  ),
)
/** 应用档位：优先用户选择，其次工程匹配档，再否则取可用第一项。 */
const applySize = computed(() => {
  const sizes = sizeValues.value
  if (selectedSize.value && sizes.includes(selectedSize.value)) {
    return selectedSize.value
  }
  if (preferredSize.value && sizes.includes(preferredSize.value)) {
    return preferredSize.value
  }
  return sizes[0] ?? ''
})
const selectedResource = computed(() => {
  if (props.previewItemsOverride.length) return null
  const thumb = selectedThumb.value as MaterialResource | null
  const size = applySize.value
  if (!size) {
    return thumb && !('url' in thumb) && thumb.resmd5 ? thumb : null
  }
  return resolveFlatAssetResource(props.resources, props.topCategoryId, thumb, size)
})
const previewResource = computed(() => {
  if (props.previewItemsOverride.length) return null
  const thumb = selectedThumb.value as MaterialResource | null
  const size = applySize.value
  if (!size) {
    return thumb && !('url' in thumb) && thumb.resmd5 ? thumb : null
  }
  return resolveFlatAssetPreviewResource(props.resources, props.topCategoryId, thumb, size)
})
const resolvedPreviewUrl = computed(() =>
  selectedThumb.value && 'url' in selectedThumb.value
    ? selectedThumb.value.url
    : props.previewUrlOverride || (previewResource.value?.resmd5 ? getMaterialPreviewUrl(previewResource.value.resmd5) : ''),
)
const thumbPreviewUrl = computed(() =>
  selectedThumb.value && 'url' in selectedThumb.value
    ? selectedThumb.value.url
    : props.previewUrlOverride || ((selectedThumb.value as MaterialResource | null)?.resmd5 ? getMaterialPreviewUrl((selectedThumb.value as MaterialResource).resmd5) : ''),
)
const previewUrl = ref('')
const previewPaneClass = computed(() => ({
  'preview-pane-horizontal': isHorizontalCategory(props.topCategoryId),
  'preview-pane-vertical': !isHorizontalCategory(props.topCategoryId),
}))
const isUiPreviewMode = computed(() => isUiMaterialCategory(props.topCategoryId))
const showInlinePreview = computed(() => !isUiPreviewMode.value)
const lightboxGalleryUrls = computed(() =>
  thumbEntries.value.map(({ item }) => {
    if ('url' in item) return item.url
    const size = applySize.value
    if (!size) return getMaterialPreviewUrl((item as MaterialResource).resmd5)
    const resource = resolveFlatAssetResource(
      props.resources,
      props.topCategoryId,
      item as MaterialResource,
      size,
    )
    return resource?.resmd5 ? getMaterialPreviewUrl(resource.resmd5) : getMaterialPreviewUrl((item as MaterialResource).resmd5)
  }),
)
const selectedThumbIndex = computed(() =>
  Math.max(0, thumbEntries.value.findIndex((entry) => entry.key === selectedThumbId.value)),
)
const canApply = computed(() => !!props.applyOverride || canApplyMaterialCategory(props.topCategoryId))
const applyResources = computed(() => {
  if (isUiMaterialCategory(props.topCategoryId)) {
    return props.resources.filter((item) => !!item.resmd5 && !String(item.extra || '').toLowerCase().endsWith('.oui'))
  }
  return selectedResource.value ? [selectedResource.value] : []
})
const alreadyApplied = computed(() => {
  if (props.applyOverride) return false
  const root = resolveMaterialUsageRoot(props.topCategoryId, workspace.virtualResourceRoots)
  return areMaterialResourcesAlreadyApplied(root, applyResources.value)
})

function resetSelectedThumb() {
  selectedThumbId.value = props.preferPreviewUrlOverride && props.previewUrlOverride ? '' : thumbEntries.value[0]?.key ?? ''
}

watch(
  () => props.goods.id,
  () => {
    previewUrl.value = ''
    selectedThumbId.value = ''
    selectedSize.value = ''
    lightboxOpen.value = false
  },
)

watch(
  thumbs,
  () => resetSelectedThumb(),
  { immediate: true },
)

watch(
  () => props.previewUrlOverride,
  () => resetSelectedThumb(),
)

watch(
  [sizeValues, preferredSize, () => props.goods.id, () => props.topCategoryId, selectedThumbId, sizeOptions],
  () => {
    selectedSize.value = resolveMaterialPreviewSelectedSize(
      sizeValues.value,
      preferredSize.value,
      selectedSize.value,
    )
  },
  { immediate: true },
)

watch(
  [resolvedPreviewUrl, thumbPreviewUrl],
  ([nextPreviewUrl, nextThumbUrl]) => {
    previewUrl.value = nextPreviewUrl || nextThumbUrl || ''
  },
  { immediate: true },
)

function handlePreviewError() {
  if (previewUrl.value && thumbPreviewUrl.value && previewUrl.value !== thumbPreviewUrl.value) {
    previewUrl.value = thumbPreviewUrl.value
    return
  }
  previewUrl.value = ''
}

function openThumbPreview(key: string) {
  selectedThumbId.value = key
  lightboxGalleryIndex.value = selectedThumbIndex.value
  if (isUiPreviewMode.value) {
    lightboxOpen.value = true
  }
}

function handleLightboxGalleryIndexUpdate(index: number) {
  lightboxGalleryIndex.value = index
  selectedThumbId.value = thumbEntries.value[index]?.key ?? selectedThumbId.value
}

const handleUiApplyProgress: UiMaterialApplyProgress = (_stage, percent, detail) => {
  applyProgressText.value = detail ? `${detail} ${percent}%` : `应用中 ${percent}%`
}

async function applyCurrentResource() {
  if (props.applyOverride) {
    applying.value = true
    applyProgressText.value = ''
    try {
      await props.applyOverride()
    } catch (error) {
      message.error(error instanceof Error ? error.message : '素材应用失败')
    } finally {
      applying.value = false
      applyProgressText.value = ''
    }
    return
  }
  if (!isUiMaterialCategory(props.topCategoryId) && !applySize.value) {
    message.warning('当前工程分辨率与素材可用档位不匹配，无法应用')
    return
  }
  if (!isUiMaterialCategory(props.topCategoryId) && !selectedResource.value) {
    message.warning('当前没有可应用的素材资源')
    return
  }
  applying.value = true
  applyProgressText.value = ''
  try {
    if (isUiMaterialCategory(props.topCategoryId)) {
      await applyUiMaterialToWorkspace({
        resources: props.resources,
        onProgress: handleUiApplyProgress,
      })
    } else {
      await applyMaterialResourceToWorkspace({
        topCategoryId: props.topCategoryId,
        goods: props.goods,
        resource: selectedResource.value!,
      })
    }
  } catch (error) {
    message.error(error instanceof Error ? error.message : '素材应用失败')
  } finally {
    applying.value = false
    applyProgressText.value = ''
  }
}
</script>

<template>
  <div class="preview-pane" :class="previewPaneClass">
    <header class="preview-header">
      <div class="preview-title">{{ goods.name }}</div>
      <div class="preview-subtitle">{{ goods.extra || goods.intro || '暂无说明' }}</div>
      <div class="preview-actions">
        <n-button v-if="showCollect" size="small" @click="emit('toggleCollect')">
          {{ collected ? '取消收藏' : '加入收藏' }}
        </n-button>
        <n-button
          size="small"
          type="primary"
          secondary
          :loading="applying"
          :disabled="(!applyOverride && !applyResources.length) || !canApply || alreadyApplied"
          @click="applyCurrentResource"
        >
          {{ applying ? applyProgressText || '应用中…' : alreadyApplied ? '已使用' : '应用' }}
        </n-button>
        <MaterialPreviewSizeSelect v-model="selectedSize" :options="sizeOptions" />
        <n-button
          size="small"
          type="primary"
          :disabled="isUiPreviewMode ? !lightboxGalleryUrls.length : !previewUrl"
          @click="lightboxOpen = true"
        >
          预览全图
        </n-button>
      </div>
    </header>
    <div v-if="applying && applyProgressText" class="apply-progress">{{ applyProgressText }}</div>

    <div v-if="showInlinePreview && previewUrl && !previewLoading" class="preview-stage">
      <img :src="previewUrl" :alt="goods.name" class="preview-image" @error="handlePreviewError" />
    </div>
    <n-empty v-else-if="showInlinePreview && previewLoading" description="正在加载预览…" class="preview-empty" />
    <n-empty v-else-if="showInlinePreview" description="当前素材没有可预览图片" class="preview-empty" />

    <div v-if="showThumbs && thumbs.length && !previewLoading" class="thumb-grid">
      <button
        v-for="{ item, key } in thumbEntries"
        :key="key"
        type="button"
        class="thumb-item"
        :class="{ 'thumb-item-active': selectedThumbId === key }"
        @click="openThumbPreview(key)"
      >
        <img
          :src="'url' in item ? item.url : getMaterialPreviewUrl(item.resmd5)"
          :alt="'url' in item ? item.extra : item.extra"
          class="thumb-image"
        />
      </button>
    </div>

    <MaterialPreviewLightbox
      v-model:show="lightboxOpen"
      v-model:selectedSize="selectedSize"
      :title="goods.name"
      caption=""
      :size-options="sizeOptions"
      :layer-urls="previewUrl ? [previewUrl] : []"
      :gallery-urls="isUiPreviewMode ? lightboxGalleryUrls : []"
      :gallery-index="lightboxGalleryIndex"
      @update:gallery-index="handleLightboxGalleryIndexUpdate"
    />
  </div>
</template>

<style scoped>
.preview-pane {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 100%;
  overflow: visible;
  padding: 8px;
  box-sizing: border-box;
  background: var(--theme-bg-panel);
  font-family: var(--material-font-family, "Microsoft YaHei", "PingFang SC", Arial, sans-serif);
  font-size: var(--material-font-size-base, 12px);
  line-height: var(--material-line-height, 1.35);
  color: var(--theme-text-primary);
}

.preview-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}

.preview-title {
  font-size: var(--material-font-size-title, 13px);
  font-weight: 600;
  color: var(--theme-text-heading);
}

.preview-subtitle,
.preview-caption {
  font-size: var(--material-font-size-small, 11px);
  color: var(--theme-text-secondary);
  line-height: var(--material-line-height, 1.35);
  word-break: break-word;
}

.preview-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
}

.apply-progress {
  font-size: var(--material-font-size-small, 11px);
  color: var(--theme-text-secondary);
  line-height: var(--material-line-height, 1.35);
  flex-shrink: 0;
}

.preview-stage {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  width: max-content;
  padding: 6px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-raised);
}

.preview-image {
  display: block;
  width: auto;
  max-width: none;
  max-height: none;
  object-fit: contain;
  background: var(--theme-bg-deep);
}

.preview-pane-vertical .preview-image {
  width: 240px;
}

.preview-pane-horizontal .preview-image {
  width: 250px;
}

.thumb-grid {
  display: grid;
  gap: 6px;
  overflow: visible;
  align-content: start;
}

.preview-pane-vertical .thumb-grid {
  grid-template-columns: repeat(3, 80px);
  grid-auto-rows: 120px;
}

.preview-pane-horizontal .thumb-grid {
  grid-template-columns: 250px;
  grid-auto-rows: 134px;
}

.thumb-item {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  cursor: pointer;
  overflow: hidden;
}

.thumb-item-active {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
}

.thumb-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-empty {
  flex-shrink: 0;
}
</style>
